import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * VinciShieldTransition — a short (≈550 ms) gold-pulse overlay that
 * appears on every route change. Replaces the abrupt "white-flash"
 * of a normal SPA nav with a cinematic dissolve.
 *
 * Behaviour:
 * • Fires on every `location.pathname` change (skips search-param-only
 *   changes and hash scrolls so filter/tab toggles stay snappy).
 * • Auto-hides after 550 ms — the page-underneath renders instantly,
 *   the shield just floats over it during the crossfade.
 * • Respects `prefers-reduced-motion` and stays out of the way for
 *   accessibility users.
 */
export function VinciShieldTransition() {
  const location = useLocation();
  const navType = useNavigationType();
  const [showing, setShowing] = useState(false);
  const [firstMount, setFirstMount] = useState(true);

  useEffect(() => {
    // Don't fire on the initial page load — the app's own splash
    // already covers that. Only fire on real navigations.
    if (firstMount) { setFirstMount(false); return; }
    // Respect user's motion preference.
    if (typeof window !== "undefined"
        && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    // Only trigger on POP (browser back/fwd) or PUSH (Link click),
    // not on same-path replaces which cause visual thrash on hash
    // scrolls and query-string tweaks.
    if (navType === "REPLACE") return;
    setShowing(true);
    const t = setTimeout(() => setShowing(false), 550);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <AnimatePresence>
      {showing && (
        <motion.div
          key="vinci-shield-transition"
          data-testid="vinci-shield-transition"
          className="fixed inset-0 z-[9997] pointer-events-none grid place-items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background:
              "radial-gradient(600px 600px at 50% 50%, rgba(216,178,62,0.16), rgba(5,5,6,0.72) 60%)",
            backdropFilter: "blur(10px) saturate(140%)",
            WebkitBackdropFilter: "blur(10px) saturate(140%)",
          }}
          aria-hidden
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: [0.85, 1.05, 0.98], opacity: [0, 1, 1] }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1],
                            times: [0, 0.55, 1] }}
            style={{ filter: "drop-shadow(0 0 60px rgba(216,178,62,0.7))" }}
          >
            <svg width="120" height="120" viewBox="0 0 200 200" aria-hidden>
              <defs>
                <linearGradient id="rtShieldGold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%"   stopColor="#7A5410" />
                  <stop offset="50%"  stopColor="#D8B23E" />
                  <stop offset="100%" stopColor="#F6E4A6" />
                </linearGradient>
              </defs>
              <path
                d="M100 12 L182 46 L182 108 C182 148 148 178 100 190 C52 178 18 148 18 108 L18 46 Z"
                fill="rgba(216,178,62,0.06)" stroke="url(#rtShieldGold)"
                strokeWidth="1.6"
              />
              <text
                x="100" y="118" textAnchor="middle"
                fill="url(#rtShieldGold)"
                style={{ fontFamily: "'Cinzel', serif", fontWeight: 900,
                          fontSize: 60, letterSpacing: "0.04em" }}
              >VI</text>
            </svg>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
